import Filter from './Filter';
import ApplicationSelection from './ApplicationSelection';
import StockAvailabilitySelection from './StockAvailabilitySelection';
import CategorySelection from './CategorySelection';
import { Box, Typography, useMediaQuery, Collapse } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useEffect, useState, useMemo } from 'react';

interface TooltipData {
  dimensions_for_sizes: {
    [sizeName: string]: {
      diameter?: string[];
      height?: string[];
      item_height?: string[];
      item_length?: string[];
      length_cm?: string[];
      width?: string[];
    };
  };
}

interface FilterCategory {
  title: string;
  available?: string[];
  filtered?: string[];
  list?: string[];
  setList?: React.Dispatch<React.SetStateAction<string[]>>;
  keyName?: string;
  paramKey?: string;
  isApplications?: boolean;
}

interface FilterSelectionProps {
  availableColours: string[];
  availableDesigns: string[];
  availableTypes: string[];
  availableBrands: string[];
  availableRanges: string[];
  availableMaterials: string[];
  availableStandards: string[];
  availableCompositions: string[];
  availablePerformances: string[];
  availableSizes: string[];
  availableShapes: string[];
  availableApplications?: string[];

  filteredColours: string[];
  filteredDesigns: string[];
  filteredTypes: string[];
  filteredBrands: string[];
  filteredRanges: string[];
  filteredMaterials: string[];
  filteredStandards: string[];
  filteredCompositions: string[];
  filteredPerformances: string[];
  filteredSizes: string[];
  filteredShapes: string[];

  colourList: string[];
  designList: string[];
  typesList: string[];
  brandList: string[];
  rangeList: string[];
  materialList: string[];
  standardsList: string[];
  compositionList: string[];
  performanceList: string[];
  sizeList: string[];
  shapeList: string[];

  setColourList: React.Dispatch<React.SetStateAction<string[]>>;
  setDesignList: React.Dispatch<React.SetStateAction<string[]>>;
  setTypesList: React.Dispatch<React.SetStateAction<string[]>>;
  setBrandList: React.Dispatch<React.SetStateAction<string[]>>;
  setRangeList: React.Dispatch<React.SetStateAction<string[]>>;
  setMaterialList: React.Dispatch<React.SetStateAction<string[]>>;
  setStandardsList: React.Dispatch<React.SetStateAction<string[]>>;
  setCompositionList: React.Dispatch<React.SetStateAction<string[]>>;
  setPerformanceList: React.Dispatch<React.SetStateAction<string[]>>;
  setSizeList: React.Dispatch<React.SetStateAction<string[]>>;
  setShapeList: React.Dispatch<React.SetStateAction<string[]>>;

  handleCheckboxChange: any;
  applicationsList: string[];
  setApplicationsList?: React.Dispatch<React.SetStateAction<string[]>>;
  applyFiltersForApplications?: (applications: string[]) => void;
  itemGroup: string;
  isLoading?: boolean;
  tooltipData?: TooltipData | null;

  // Cross-category extras (opt-in; used by the site-wide search results modal,
  // mirroring trade's PDF multifilter). Default off so page-level multifilters
  // are unchanged.
  //   Availability: a single "In stock" checkbox pinned as the first section.
  showAvailability?: boolean;
  inStockOnly?: boolean;
  onStockChange?: (inStockOnly: boolean) => void;
  //   Category: an item-group checkbox list pinned directly below Availability.
  showCategory?: boolean;
  selectedCategories?: string[];
  availableCategories?: { [key: string]: string };
  onCategoryChange?: (itemGroups: string[]) => void;
  //   Title for the web_type facet. Defaults to 'Category'; the search modal
  //   passes 'Types' so it doesn't clash with the item-group Category filter
  //   (matching trade, where web_type is "Types" and item groups are "Category").
  webTypeTitle?: string;
}

const FilterSelection: React.FC<FilterSelectionProps> = ({
                                                           availableColours,
                                                           availableDesigns,
                                                           availableTypes,
                                                           availableBrands,
                                                           availableRanges,
                                                           availableMaterials,
                                                           availableStandards,
                                                           availableCompositions,
                                                           availablePerformances,
                                                           availableSizes,
                                                           availableShapes,
                                                           availableApplications = [],
                                                           filteredColours,
                                                           filteredDesigns,
                                                           filteredTypes,
                                                           filteredBrands,
                                                           filteredRanges,
                                                           filteredMaterials,
                                                           filteredStandards,
                                                           filteredCompositions,
                                                           filteredPerformances,
                                                           filteredSizes,
                                                           filteredShapes,
                                                           colourList,
                                                           designList,
                                                           typesList,
                                                           brandList,
                                                           rangeList,
                                                           materialList,
                                                           standardsList,
                                                           compositionList,
                                                           performanceList,
                                                           sizeList,
                                                           shapeList,
                                                           setColourList,
                                                           setDesignList,
                                                           setTypesList,
                                                           setBrandList,
                                                           setRangeList,
                                                           setMaterialList,
                                                           setStandardsList,
                                                           setCompositionList,
                                                           setPerformanceList,
                                                           setSizeList,
                                                           setShapeList,
                                                           handleCheckboxChange,
                                                           applicationsList,
                                                           setApplicationsList,
                                                           applyFiltersForApplications,
                                                           itemGroup,
                                                           isLoading = false,
                                                           tooltipData = null,
                                                           showAvailability = false,
                                                           inStockOnly = false,
                                                           onStockChange,
                                                           showCategory = false,
                                                           selectedCategories = [],
                                                           availableCategories = {},
                                                           onCategoryChange,
                                                           webTypeTitle = 'Category',
                                                         }) => {
  const [visibility, setVisibility] = useState<{ [key: string]: boolean }>({
    Availability: false,
    Applications: false,
    Brand: false,
    Category: false,
    Collections: false,
    Colour: false,
    Composition: false,
    Design: false,
    Material: false,
    Performance: false,
    Shape: false,
    Standards: false,
    Size: false,
    Types: false,
  });

  const isMobile = useMediaQuery('(max-width:600px)');

  useEffect(() => {
    if (isMobile) {
      setVisibility((prevState) =>
        Object.keys(prevState).reduce((acc, key) => ({ ...acc, [key]: false }), {})
      );
    }
  }, [isMobile]);

  const filterCategories: FilterCategory[] = useMemo(
    () => [
      {
        title: 'Applications',
        isApplications: true,
      },
      {
        title: 'Brand',
        available: availableBrands,
        filtered: filteredBrands,
        list: brandList,
        setList: setBrandList,
        keyName: 'brand',
        paramKey: 'brand',
      },
      {
        title: webTypeTitle,
        available: availableTypes,
        filtered: filteredTypes,
        list: typesList,
        setList: setTypesList,
        keyName: 'types',
        paramKey: 'types',
      },
      {
        title: 'Collections',
        available: availableRanges,
        filtered: filteredRanges,
        list: rangeList,
        setList: setRangeList,
        keyName: 'range',
        paramKey: 'range',
      },
      {
        title: 'Colour',
        available: availableColours,
        filtered: filteredColours,
        list: colourList,
        setList: setColourList,
        keyName: 'colours',
        paramKey: 'colours',
      },
      {
        title: 'Composition',
        available: availableCompositions,
        filtered: filteredCompositions,
        list: compositionList,
        setList: setCompositionList,
        keyName: 'composition',
        paramKey: 'composition',
      },
      {
        title: 'Design',
        available: availableDesigns,
        filtered: filteredDesigns,
        list: designList,
        setList: setDesignList,
        keyName: 'design_style',
        paramKey: 'design_style',
      },
      {
        title: 'Material',
        available: availableMaterials,
        filtered: filteredMaterials,
        list: materialList,
        setList: setMaterialList,
        keyName: 'material',
        paramKey: 'material',
      },
      {
        title: 'Performance',
        available: availablePerformances,
        filtered: filteredPerformances,
        list: performanceList,
        setList: setPerformanceList,
        keyName: 'performance',
        paramKey: 'performance',
      },
      {
        title: 'Shape',
        available: availableShapes,
        filtered: filteredShapes,
        list: shapeList,
        setList: setShapeList,
        keyName: '',
        paramKey: 'shapes',
      },
      {
        title: 'Size',
        available: availableSizes,
        filtered: filteredSizes,
        list: sizeList,
        setList: setSizeList,
        keyName: '',
        paramKey: 'sizes',
      },
      {
        title: 'Standards',
        available: availableStandards,
        filtered: filteredStandards,
        list: standardsList,
        setList: setStandardsList,
        keyName: 'standards',
        paramKey: 'standards',
      },
    ],
    [
      availableTypes, availableDesigns, availableColours, availableMaterials, availableSizes, availableShapes, availableBrands, availableRanges, availableStandards, availableCompositions, availablePerformances,
      filteredTypes, filteredDesigns, filteredColours, filteredMaterials, filteredSizes, filteredShapes, filteredBrands, filteredRanges, filteredStandards, filteredCompositions, filteredPerformances,
      typesList, designList, colourList, materialList, sizeList, shapeList, brandList, rangeList, standardsList, compositionList, performanceList,
      setTypesList, setDesignList, setColourList, setMaterialList, setSizeList, setShapeList, setBrandList, setRangeList, setStandardsList, setCompositionList, setPerformanceList,
      webTypeTitle,
    ]
  );

  const getFilteredOptions = useMemo(
    () => (available?: string[], filtered?: string[], category?: string) => {
      if (!available) return [];
      if (!applicationsList?.length || !available?.length) return available;
      if (!filtered?.length && available?.length) {
        if (category === webTypeTitle) return available.filter((item) => item !== '-');
        return available;
      }
      const lowerFiltered = (filtered ?? []).map((value: string) => value.toLowerCase());
      return available.filter((item) => {
        if (category === webTypeTitle && item === '-') return false;
        return lowerFiltered.includes(item.toLowerCase());
      });
    },
    [applicationsList, webTypeTitle]
  );

  const shouldEnableSizeTooltips = (category: FilterCategory) => {
    if (category.title !== 'Size') return false;
    if (itemGroup === '004') return true;
    if (itemGroup === '014') {
      return applicationsList.some((app) => app.toLowerCase().includes('wall art'));
    }
    return false;
  };

  return (
    <Box
      sx={{
        opacity: isLoading ? 0.7 : 1,
        transition: 'opacity 0.2s ease',
        position: 'relative',
      }}
    >
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 1,
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '4px',
          }}
        >
          <Typography sx={{ color: '#666', fontSize: '14px', fontStyle: 'italic' }}>
            Updating filters...
          </Typography>
        </Box>
      )}

      {/* Availability - pinned as the first section (cross-category views only,
          e.g. the site-wide search results modal). Mirrors trade's PDF multifilter. */}
      {showAvailability && (
        <Box sx={{ mt: '1.5rem' }}>
          <Box
            onClick={() => setVisibility((prev) => ({ ...prev, Availability: !prev.Availability }))}
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', mb: isMobile ? 1 : 2 }}
          >
            <Typography fontWeight="bold" fontSize="18px" mb={0}>Availability</Typography>
            <Box sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              {visibility.Availability ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </Box>
          </Box>
          <Collapse in={visibility.Availability} timeout="auto" unmountOnExit>
            <StockAvailabilitySelection
              showInStock={inStockOnly}
              onStockChange={onStockChange || (() => {})}
              isLoading={isLoading}
            />
          </Collapse>
        </Box>
      )}

      {/* Category (item groups) - pinned directly below Availability. Only shown
          on cross-category views where products span multiple item groups. */}
      {showCategory && Object.keys(availableCategories || {}).length > 0 && (
        <Box sx={{ mt: '1.5rem' }}>
          <Box
            onClick={() => setVisibility((prev) => ({ ...prev, Category: !prev.Category }))}
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', mb: isMobile ? 1 : 2 }}
          >
            <Typography fontWeight="bold" fontSize="18px" mb={0}>Category</Typography>
            <Box sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              {visibility.Category ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </Box>
          </Box>
          <Collapse in={visibility.Category} timeout="auto" unmountOnExit>
            <CategorySelection
              selectedCategories={selectedCategories}
              availableCategories={availableCategories}
              onCategoryChange={onCategoryChange || (() => {})}
              isLoading={isLoading}
            />
          </Collapse>
        </Box>
      )}

      {filterCategories.map((category) => {
        if (category.isApplications) {
          return (
            <ApplicationSelection
              key={category.title}
              availableApplications={availableApplications}
              handleCheckboxChange={handleCheckboxChange}
              applicationsList={applicationsList}
              setApplicationsList={setApplicationsList || (() => {})}
              itemGroup={itemGroup}
              applyFiltersForApplications={applyFiltersForApplications || (() => {})}
            />
          );
        }

        const filteredOptions = getFilteredOptions(category.available, category.filtered, category.title);

        if (!filteredOptions?.length || (category.title === 'Shape' && itemGroup !== '004')) {
          return null;
        }

        return (
          <Box
            key={category.title}
            sx={{ mt: '1.5rem', borderBottom: isMobile ? '1px solid #f0f0f0' : 'none', pb: isMobile ? 1 : 0 }}
          >
            <Box
              onClick={() => setVisibility((prev) => ({ ...prev, [category.title]: !prev[category.title] }))}
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', mb: isMobile ? 1 : 2 }}
            >
              <Typography fontWeight="bold" fontSize="18px" mb={0}>
                {category.title}
              </Typography>
              <Box sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                {visibility[category.title] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </Box>
            </Box>
            <Collapse in={visibility[category.title]} timeout="auto" unmountOnExit>
              <Filter
                filterType={filteredOptions}
                filteredOptions={category.filtered || []}
                handleCheckboxChange={handleCheckboxChange}
                list={category.list || []}
                setList={category.setList || (() => {})}
                keyName={category.keyName || ''}
                paramKey={category.paramKey || ''}
                tooltipData={shouldEnableSizeTooltips(category) ? tooltipData : null}
                itemGroup={itemGroup}
                applicationsList={applicationsList}
              />
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
};

export default FilterSelection;